import { useState } from 'react';
import { Phone, MessageSquare, FileText, Activity, ChevronDown, ChevronRight } from 'lucide-react';
import type { TaskTimelineEvent } from '@vagusx/clients';
import { Skeleton } from '@/components/ui/skeleton';
import { parseMentionBody } from '@/components/custom/mention-input';
import { useTaskTimeline } from './use-task-overview';
import { historyNote } from './call-follow-up-display';

interface ActivityTimelineProps {
  taskId: string | undefined;
  /** Initial collapsed state. Default: collapsed. */
  defaultCollapsed?: boolean;
  /**
   * Open the Call detail sheet for this interaction. The timeline no longer
   * owns a sheet — `TaskOverviewTab` renders the single one, keyed off the
   * `?call=` URL param, so this and `LastCallSection` cannot each stack their
   * own sheet on the same id.
   */
  onOpenCall: (interactionId: string) => void;
}

/**
 * Unified activity timeline merging interactions, system history, and
 * notes. Newest-first. Each row's icon + secondary line is driven by
 * the discriminator (`kind`) on the event payload.
 */
export function ActivityTimeline({
  taskId,
  defaultCollapsed = true,
  onOpenCall,
}: ActivityTimelineProps): React.ReactElement {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const { data, isLoading, isError } = useTaskTimeline(taskId);

  return (
    <section className="rounded-lg border bg-card">
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-accent/50"
        aria-expanded={!collapsed}
      >
        {collapsed ? (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
        <Activity className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold">Activity</h3>
        {data && (
          <span className="text-xs text-muted-foreground">
            · {data.events.length} event{data.events.length === 1 ? '' : 's'}
          </span>
        )}
      </button>
      {!collapsed && (
        <div className="border-t px-4 py-3">
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : isError ? (
            <p className="text-sm text-destructive">Couldn't load activity.</p>
          ) : !data || data.events.length === 0 ? (
            <p className="text-sm text-muted-foreground">No activity yet.</p>
          ) : (
            <>
              <ol className="space-y-3">
                {data.events.map((evt) => (
                  <TimelineRow
                    key={`${evt.kind}-${evt.id}`}
                    event={evt}
                    onSelectInteraction={onOpenCall}
                  />
                ))}
              </ol>
              {/* Server caps each source at a fixed limit so a runaway
                  task can't blow up the response. Surface that here so
                  the user knows the timeline is truncated — without
                  this, very long histories look like the only events
                  are the recent ones. Fetch-more is a follow-up. */}
              {(data.hasMoreByKind.interaction ||
                data.hasMoreByKind.history ||
                data.hasMoreByKind.note) && (
                <p className="mt-3 border-t pt-3 text-xs text-muted-foreground">
                  Showing recent activity only. Older events exist but aren&apos;t loaded yet.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}

interface TimelineRowProps {
  event: TaskTimelineEvent;
  /** Called when an interaction row is clicked. The parent owns the
   *  selected-id state so the detail sheet mounts once per timeline. */
  onSelectInteraction: (interactionId: string) => void;
}

function TimelineRow({ event, onSelectInteraction }: TimelineRowProps): React.ReactElement {
  const body = (
    <>
      <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted">
        <EventIcon event={event} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-medium">{eventTitle(event)}</span>
          <span className="text-xs text-muted-foreground">
            {new Date(event.timestamp).toLocaleString()}
          </span>
        </div>
        <EventBody event={event} />
      </div>
    </>
  );

  // Interaction rows open the detail drawer (recording + transcript)
  // in place — no route change. event.id IS the interaction id (set in
  // TaskOverviewService.getTimeline). Other event kinds (note, history)
  // are not navigable today — their detail lives in the row body itself.
  if (event.kind === 'interaction') {
    return (
      <li>
        <button
          type="button"
          onClick={() => onSelectInteraction(event.id)}
          className="-mx-2 flex w-full gap-3 rounded-md px-2 py-1 text-left transition-colors hover:bg-accent/50"
        >
          {body}
        </button>
      </li>
    );
  }

  return <li className="flex gap-3">{body}</li>;
}

function EventIcon({ event }: { event: TaskTimelineEvent }): React.ReactElement {
  if (event.kind === 'interaction') {
    return event.payload.channel === 'sms' ? (
      <MessageSquare className="h-3.5 w-3.5" />
    ) : (
      <Phone className="h-3.5 w-3.5" />
    );
  }
  if (event.kind === 'note') return <FileText className="h-3.5 w-3.5" />;
  return <Activity className="h-3.5 w-3.5" />;
}

function eventTitle(event: TaskTimelineEvent): string {
  if (event.kind === 'interaction') {
    const direction = event.payload.direction ?? 'unknown';
    return `${event.payload.channel} ${direction}${
      event.payload.agentName ? ` · ${event.payload.agentName}` : ''
    }`;
  }
  if (event.kind === 'history') {
    return event.payload.action.replace(/_/g, ' ');
  }
  return 'Internal note';
}

function EventBody({ event }: { event: TaskTimelineEvent }): React.ReactElement | null {
  if (event.kind === 'interaction') {
    return (
      <div className="text-xs text-muted-foreground">
        status: {event.payload.status}
        {event.payload.durationSeconds !== null
          ? ` · ${formatDuration(event.payload.durationSeconds)}`
          : ''}
        {event.payload.summary ? ` — ${event.payload.summary}` : ''}
      </div>
    );
  }
  if (event.kind === 'history') {
    // Server pre-composes a human-readable line per action ("Assigned
    // to Brianna", "Rachel closed the task"). We render it as the row
    // summary, mirroring the interaction-summary line above so every
    // event row has consistent shape: title on top, sentence below.
    const summary =
      event.payload.description ??
      [
        event.payload.actorName,
        event.payload.fromStatus && event.payload.toStatus
          ? `${event.payload.fromStatus} → ${event.payload.toStatus}`
          : null,
        event.payload.comment,
        event.payload.reason,
      ]
        .filter(Boolean)
        .join(' · ');
    // A note a call or a campaign left (docs/call-follow-ups.md) carries what
    // was said in its comment: shown in full under the line, since it is the
    // whole point of the entry (a callback's number, a message, a verdict).
    const note = historyNote(event.payload);
    if (!summary && !note) return null;
    return (
      <div className="space-y-1">
        {summary && <div className="text-xs text-muted-foreground">{summary}</div>}
        {note && <div className="whitespace-pre-line text-xs text-foreground">{note}</div>}
      </div>
    );
  }
  // Note — render with mention markers expanded.
  return (
    <div className="mt-0.5 text-sm leading-relaxed">
      {parseMentionBody(event.payload.body).map((chunk, i) =>
        chunk.type === 'mention' ? (
          <strong key={i} className="text-primary">
            @{chunk.text}
          </strong>
        ) : (
          <span key={i}>{chunk.text}</span>
        )
      )}
    </div>
  );
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
